import { Component, OnInit, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { NewRecipientModel, NewRecipientStep2Model } from '@pg-app/client/manage-recipient/models/recipient.model';
import { RecipientService } from '@pg-app/client/manage-recipient/services/recipient.service';
import { ToastrService } from 'ngx-toastr';
import { CommonDialogComponent } from '@pg-app/common/common-dialog/common-dialog.component';
import { RecipientInfoService } from '@pg-app/client/manage-recipient/services/recipient-info.service';
import { SendPaymentInstructionService } from '@pg-app/client/send-payment-instruction/services/send-payment-instruction.service';
import { Observable } from 'rxjs';
import { startWith, debounceTime, map, distinctUntilChanged } from 'rxjs/operators';
import { Regex } from '@pg-app/shared/constants/regex';
import { swiftCodeValidator } from '@pg-app/client/manage-recipient/constants/swift-code-validator';
import { AddRecipientFrom, countryCodes, currencyCodes } from '@pg-app/client/manage-recipient/constants/add-recipient-constants';
import { BankAccountTypes } from '@pg-app/client/payment/constants/bank-account-type-list';
import { fasterPayGbpBankDetailsValidator } from '@pg-app/client/manage-recipient/constants/fasterpay-gbp-validator';
import { swiftOrBankCodeValidator } from '@pg-app/client/manage-recipient/constants/swift-bank-validator';
import { AddBeneficiaryStep } from '@pg-app/client/manage-recipient/constants/add-beneficiary-constant';
import { ShareService } from '@nttp/common-service/share-service';
import { BankingTypes } from '@pg-app/shared/constants/banking-types-constants';
import { BeneficiarySourceEnum } from '@pg-app/shared/enums/beneficiary-source.enum';
import { format } from 'date-fns';

@Component({
  selector: 'np-add-new-recipient-step-2',
  templateUrl: './step-2.component.html',
  styleUrls: ['./step-2.component.scss']
})
export class AddNewRecipientStep2Component implements OnInit {
  formStep2: FormGroup;
  formStep3: FormGroup;

  @Input() action = AddRecipientFrom.ManageRecipient;
  @Input() bankDto = null;
  @Input() remitterRequired = false;
  @Input() beneficiarySource: BeneficiarySourceEnum;

  addRecipientFrom = AddRecipientFrom;

  countries = [];
  saveForFuture = false;

  validationOk = false;
  isFasterPayandGBP = false;
  isIBANRequired = false;
  isBankCodeAndAccountMandatory = false;
  isFasterPay = false;
  isBankCode9Digits = false;
  // Purpose of payment
  purposeOfPayments = [];
  purposeOfPaymentsPipe: Observable<Array<any>>;
  purposeOfPaymentSelected: any;

  bankAccountTypes = BankAccountTypes;
  validatedBankDetails: NewRecipientStep2Model;

  constructor(
    private fb: FormBuilder,
    private newRecipientModel: NewRecipientModel,
    private recipientService: RecipientService,
    private recipientInfo: RecipientInfoService,
    private toastrService: ToastrService,
    private dialog: MatDialog,
    private sendPaymentInstructionService: SendPaymentInstructionService,
    private shareService: ShareService
  ) { }

  ngOnInit() {
    // banking config from API
    const bankConfig = this.newRecipientModel.bankConfig;

    this.countries = this.newRecipientModel.step1.countries;
    const country = this.newRecipientModel.step1.country;
    const currency = this.newRecipientModel.step1.currency;

    const bankAccountTypeMandatory = !!bankConfig.requireAccountType.find(p => p.countryCode === country.code && p.currencyCode === currency.code);
    const bankAccountTypeValidators = bankAccountTypeMandatory ? [Validators.required] : [];

    this.formStep3 = this.fb.group({
      bankName: [''],
      address1: [''],
      address2: [''],
      address3: [''],
      city: [''],
      stateProvince: [''],
      zipPostalCode: [''],
      country: [this.newRecipientModel.step1.country.id],
      reference: ['', [Validators.maxLength(70), Validators.pattern(Regex.nonSwiftCharacters)]],
      mandate: [false],
      purposeOfPayment: [''],
      bankAccountType: ['', bankAccountTypeValidators]
    });

    this.formStep2 = this.fb.group({
      sortCode: ['', [Validators.pattern(Regex.sixDigitPattern)]],
      accountNumber: [''],
      iban: [''],
      bankCode: [''],
      swiftCode: ['', [Validators.pattern(Regex.swiftCodePattern)]],
    });

    if (this.bankDto) {
      this.formStep2.patchValue({
        bankCode: this.bankDto.bankCode,
        accountNumber: this.bankDto.accountNumber,
        swiftCode: this.bankDto.sWiftCode,
        iban: this.bankDto.iBan,
        sortCode: this.bankDto.sortCodeUK,
      });

      this.formStep3.patchValue({
        bankName: this.bankDto.name,
        address1: this.bankDto.address1,
        address2: this.bankDto.address2,
        address3: this.bankDto.address3,
        city: this.bankDto.city,
        stateProvince: this.bankDto.provincee,
        zipPostalCode: this.bankDto.postCode,
        reference: this.bankDto.reference,
        bankAccountType: this.bankDto.bankAccountType,
      });

    }

    this.formStep2.valueChanges.pipe(debounceTime(450)).subscribe(() => {
      this.validationOk = false;
      this.resetBankDetailForm();
    });

    if (this.action !== AddRecipientFrom.Bank) {
      // purpose of payment is not saved in Bank detail of Partner
      this.getPurposeOfPayments(this.newRecipientModel.step1.country.id, this.newRecipientModel.step1.currency.code);
    }

    this.getBankingType(country, currency, bankConfig);
  }

  changeChecked(event: any) {
    if (event.checked) {
      this.checkIfMandateExists();
    } else {
      this.newRecipientModel.step2.mandate = this.formStep3.get('mandate').value;
    }
  }

  saveForFutureMandate(event: any) {
    if (!event.checked) {
      this.formStep3.get('mandate').patchValue(false);
    }
  }

  checkIfMandateExists() {
    const buyCurrency = this.newRecipientModel.step1.currency.code;
    this.recipientService.getUniqueRecipientPerCurrency(buyCurrency).subscribe(res => {
      this.recipientInfo.recipientMandateExist = res.receipient;

      if (res && res.receipient) {
        this.showDialog(res.receipient.name);
      } else {
        this.toastrService.info('Beneficiary will be selected as mandate');
        this.formStep3.get('mandate').patchValue(true);
        this.newRecipientModel.step2.mandate = this.formStep3.get('mandate').value;
      }
    });
  }

  public showDialog(recipientName) {
    const dialogRef = this.dialog.open(CommonDialogComponent, {
      disableClose: true,
      data: {
        title: `A mandate already exists for ${this.newRecipientModel.step1.currency.code} to the following beneficiary: ${recipientName}.`
          + `<br>Would you like to replace the current mandate with this new beneficiary?`,
        dialogType: 2
      }
    });

    dialogRef.afterClosed()
      .subscribe((result) => {
        this.formStep3.get('mandate').patchValue(result);
      });
  }

  back() {
    this.newRecipientModel.currentStep = AddBeneficiaryStep.STEP1;
  }

  // Ticket 2925 - Apply Financial
  validate() {
    this.formStep2.markAllAsTouched();
    if (!this.formStep2.valid) {
      return;
    }

    const iBan = this.formStep2.get('iban').value;
    const bankCode = this.formStep2.get('bankCode').value;
    const swiftCode = this.formStep2.get('swiftCode').value;
    const accountNumber = this.formStep2.get('accountNumber').value;
    const sortCode = this.formStep2.get('sortCode').value;

    const countryCode = this.newRecipientModel.step1.country.code;
    const currencyCode = this.newRecipientModel.step1.currency.code;

    let param = {
      countryCode: countryCode,
      currencyCode: currencyCode,
      accountNumber: null,
      sortCode: null,
      bankCode: null,
      iban: null,
      swiftCode: null
    }

    if (this.isFasterPayandGBP) {
      if (iBan) {
        param.iban = iBan;
      } else {
        param.sortCode = sortCode;
        param.accountNumber = accountNumber;
      }
    } else if (this.isIBANRequired) {
      param.iban = iBan;
    } else if (!this.isBankCodeAndAccountMandatory) {
      param.accountNumber = accountNumber;
      if (bankCode) {
        param.bankCode = bankCode;
      } else {
        param.swiftCode = swiftCode;
      }
    } else {
      param.accountNumber = accountNumber;
      param.bankCode = bankCode;
    }

    this.validationOk = false;

    this.recipientService.validate(param).subscribe(
      data => {
        if (!data) {
          this.toastrService.error(`Unable to validate. Please check the details and try again`);
          return;
        }

        if (data.CountryCode !== countryCode) {
          this.toastrService.error('Incorrect bank country. Please correct the bank country to ' + data.countryCode);
          return;
        }

        this.formStep3.patchValue({
          bankName: data.Bank_Name,
          address1: data.Address1,
          address2: data.Address2,
          address3: data.Address3,
          city: data.City,
          stateProvince: data.State_Province,
          zipPostalCode: data.Zip_Postal,
          reference: data.Reference
        });
        this.savingBankDetails(data);

        this.validationOk = true;
      },
      err => {
        this.validatedBankDetails = undefined;
      }
    );
  }

  private savingBankDetails(data) {
    this.validatedBankDetails = {
      IBAN: data.IBan,
      sortCode: data.SortCode,
      accountNumber: data.AccountNumber,
      bankCode: data.BankCode,
      SWIFTCode: data.SwiftCodeBic,

      bankName: data.Bank_Name,
      address1: data.Address1,
      address2: data.Address2,
      address3: data.Address3,
      city: data.City,
      stateProvince: data.State_Province,
      zipPostalCode: data.Zip_Postal,
      reference: data.Reference,

      isSendOneOffPayment: undefined,
      BIC: undefined,
      mandate: undefined,
      purposeOfPayment: undefined,

      acceptSEPA: data.AcceptSEPA
    };
  }

  private filterSearch(value: any): any[] {
    const filterValue = !value ? '' : value.toLowerCase();

    const pp = this.purposeOfPayments.find(x => {
      const combineValue = `${x.code} - ${x.description}`;
      return combineValue.toLowerCase() === filterValue;
    });

    this.purposeOfPaymentSelected = pp ? pp : null;

    return this.purposeOfPayments.filter(x => {
      const combineValue = `${x.code} - ${x.description}`;
      return combineValue.toLowerCase().indexOf(filterValue) !== -1;
    });
  }

  private getPurposeOfPayments(countryId: any, currencyCode: any) {
    if (currencyCode == "CNY") {
      this.sendPaymentInstructionService.getPaymentCodesForCNY().subscribe(data => {
        this.purposeOfPayments = data;
        this.updateValueAndValidityForPurposeOfPayment();
      });
    } else if (countryId) {
      this.sendPaymentInstructionService.getPaymentCodes(countryId).subscribe(data => {
        this.purposeOfPayments = data;
        this.updateValueAndValidityForPurposeOfPayment();
      });
    }
  }

  private updateValueAndValidityForPurposeOfPayment() {
    if (this.purposeOfPayments.length > 0) {
      this.formStep3.get('purposeOfPayment').setValidators([Validators.required]);
      this.formStep3.get('purposeOfPayment').updateValueAndValidity();
      this.purposeOfPaymentsPipe = this.formStep3.get('purposeOfPayment').valueChanges.pipe(
        startWith(''),
        debounceTime(350),
        distinctUntilChanged(),
        map(value => this.filterSearch(value)
        ));
    } else {
      this.formStep3.get('purposeOfPayment').clearValidators();
    }
  }

  public isPurposeOfPaymentValid() {
    return this.purposeOfPayments.length === 0 || (this.purposeOfPayments.length > 0 && this.purposeOfPaymentSelected);
  }

  public selectPurposeOfPayment(event: MatAutocompleteSelectedEvent): void {
    this.purposeOfPaymentSelected = event.option.value;     // Save the object
    this.formStep3.get('purposeOfPayment').setValue(event.option.value.code + ' - ' + event.option.value.description);  // For display purposes only
  }

  nextStep3() {
    this.formStep2.markAllAsTouched();
    this.formStep3.markAllAsTouched();

    if (!this.formStep2.valid || !this.formStep3.valid || !this.validationOk) {
      return;
    }

    if (this.purposeOfPayments.length > 0 && !this.purposeOfPaymentSelected) {
      return;
    }

    this.newRecipientModel.step2.IBAN = this.validatedBankDetails.IBAN;
    this.newRecipientModel.step2.sortCode = this.validatedBankDetails.sortCode;
    this.newRecipientModel.step2.accountNumber = this.validatedBankDetails.accountNumber;
    this.newRecipientModel.step2.bankCode = this.validatedBankDetails.bankCode;
    this.newRecipientModel.step2.SWIFTCode = this.validatedBankDetails.SWIFTCode;

    this.newRecipientModel.step2.bankName = this.validatedBankDetails.bankName;
    this.newRecipientModel.step2.address1 = this.validatedBankDetails.address1;
    this.newRecipientModel.step2.address2 = this.recipientService.cleanText(this.validatedBankDetails.address2);
    this.newRecipientModel.step2.address3 = this.recipientService.cleanText(this.validatedBankDetails.address3);
    this.newRecipientModel.step2.city = this.validatedBankDetails.city;
    this.newRecipientModel.step2.stateProvince = this.recipientService.cleanText(this.validatedBankDetails.stateProvince);
    this.newRecipientModel.step2.zipPostalCode = this.validatedBankDetails.zipPostalCode;

    this.newRecipientModel.step2.reference = this.recipientService.cleanText(this.formStep3.get('reference').value);
    this.newRecipientModel.step2.isSendOneOffPayment = this.action === AddRecipientFrom.ManageRecipient ? false : !this.saveForFuture;
    this.newRecipientModel.step2.mandate = this.formStep3.get('mandate').value;
    this.newRecipientModel.step2.purposeOfPayment = this.purposeOfPaymentSelected ? this.purposeOfPaymentSelected.id : '';
    this.newRecipientModel.step2.bankAccountType = this.formStep3.get('bankAccountType').value;
    this.newRecipientModel.step2.acceptSEPA = this.validatedBankDetails.acceptSEPA;

    if (this.action !== AddRecipientFrom.Bank) {
      // Validate recipient and bank information entered so far and not apply for bank detail of partner
      const recipientModel = {
        receipient: {
          name: this.newRecipientModel.step1.recipientName,
          countryId: this.newRecipientModel.step1.recipientCountry.id || null,
          currencyId: this.newRecipientModel.step1.currency.id,
          emailAddress: this.newRecipientModel.step1.email,
          isNotified: this.newRecipientModel.step1.notification === 'yes',
          status: 1,
          paymentMandate: this.newRecipientModel.step2.mandate,
          isPEPSanctions: false,
          address1: this.newRecipientModel.step1.recipientAddress1 || null,
          address2: this.newRecipientModel.step1.recipientAddress2 || null,
          beneficiaryType: this.newRecipientModel.step1.beneficiaryType,
          beneficiaryCity: this.newRecipientModel.step1.beneficiaryCity || null,
          beneficiaryStateProvince: this.newRecipientModel.step1.beneficiaryState || null,
          beneficiaryPostCode: this.newRecipientModel.step1.beneficiaryPostCode || null,
          beneficiaryTaxId: this.newRecipientModel.step1.beneficiaryTaxId || null,
        },
        ultimateRemitter: null,
        existingUltimateRemitterIdSelected: this.newRecipientModel.existingUltimateRemitterIdSelected,
        mainBank: {
          name: this.newRecipientModel.step2.bankName,
          accountNumber: this.newRecipientModel.step2.accountNumber || null,
          address1: this.newRecipientModel.step2.address1,
          address2: this.newRecipientModel.step2.address2 || null,
          address3: this.newRecipientModel.step2.address3 || null,
          city: this.newRecipientModel.step2.city || null,
          province: this.newRecipientModel.step2.stateProvince || null,
          postCode: this.newRecipientModel.step2.zipPostalCode || null,
          countryId: this.newRecipientModel.step1.country.id || null,
          status: 1,
          iBan: this.newRecipientModel.step2.IBAN || null,
          reference: this.newRecipientModel.step2.reference || null,
          sWiftCode: this.newRecipientModel.step2.SWIFTCode || null,
          sortCodeUK: this.newRecipientModel.step2.sortCode || null,
          bankCode: this.newRecipientModel.step2.bankCode || null,
          purposeOfPaymentId: this.newRecipientModel.step2.purposeOfPayment || null,
          bankAccountType: this.newRecipientModel.step2.bankAccountType,
        },
        isIntermediary: false,
        intermediaryBank: null,
        isSendOneOffPayment: this.newRecipientModel.step2.isSendOneOffPayment,
        clientId: null
      };
  
      if (!recipientModel.existingUltimateRemitterIdSelected && this.beneficiarySource === BeneficiarySourceEnum.payment) {
        recipientModel.ultimateRemitter = {
          Required: this.remitterRequired,
          Type: this.newRecipientModel.remitterDetail.ultimateRemitterType,
          Address: this.newRecipientModel.remitterDetail.ultimateRemitterAddress,
          DateOfBirthString: format(this.newRecipientModel.remitterDetail.ultimateRemitterDOB, 'YYYYMMDD'),
          CompanyName: this.newRecipientModel.remitterDetail.ultimateRemitterCompanyName,
          CompanyNumber: this.newRecipientModel.remitterDetail.ultimateRemitterCompanyNumber,
          FirstName: this.newRecipientModel.remitterDetail.ultimateRemitterFirstName,
          LastName: this.newRecipientModel.remitterDetail.ultimateRemitterLastName,
          City: this.newRecipientModel.remitterDetail.ultimateRemitterCity,
          PostCode: this.newRecipientModel.remitterDetail.ultimateRemitterPostCode,
          StateProvince: this.newRecipientModel.remitterDetail.ultimateRemitterState,
          CountryId: this.newRecipientModel.remitterDetail.ultimateRemitterCountryId,
        }
      }
  
      this.recipientService.validateRecipientBankModel(recipientModel).subscribe(
        () => {
          this.newRecipientModel.currentStep = this.remitterRequired
                                                    && this.action !== AddRecipientFrom.Bank
                                                    && this.beneficiarySource === BeneficiarySourceEnum.payment
                                              ? AddBeneficiaryStep.STEP3
                                              : AddBeneficiaryStep.Authy;
      });
    } else {
      this.newRecipientModel.currentStep = this.remitterRequired && this.action !== AddRecipientFrom.Bank && this.beneficiarySource === BeneficiarySourceEnum.payment
      ? AddBeneficiaryStep.STEP3 : AddBeneficiaryStep.Authy;
    }
  }

  resetBankDetailForm() {
    this.formStep3.patchValue({
      bankName: '',
      address1: '',
      address2: '',
      address3: '',
      city: '',
      stateProvince: '',
      zipPostalCode: '',
    });
  }

  getBankingType(country, currency, bankConfig) {
    let dto = {
      countryId: country.id,
      currencyId: currency.id
    };
    this.shareService.getBankingType(dto).subscribe(bankingType => {
      this.isFasterPay = country.isFasterPay;
      this.isFasterPayandGBP = bankingType === BankingTypes.Case1_FasterPay_GBP;
      this.isIBANRequired = bankingType === BankingTypes.Case2_IBAN;
      this.isBankCodeAndAccountMandatory = bankingType === BankingTypes.Case3_BankCode;
      this.isBankCode9Digits = !!bankConfig.require9DigitBankCode.find(p => p.countryCode === country.code && p.currencyCode === currency.code);

      const accountNumberPattern = this.isFasterPay ? Regex.eightDigitPattern : Regex.alphabetPattern;
      const bankCodePattern = this.isBankCode9Digits ? Regex.nineDigitPattern : Regex.alphabetPattern;

      // form validation
      const formValidator = this.isFasterPayandGBP ? fasterPayGbpBankDetailsValidator() :
        !this.isFasterPayandGBP && !this.isIBANRequired && !this.isBankCodeAndAccountMandatory ?
          [
            swiftCodeValidator(),
            swiftOrBankCodeValidator()
          ] : [];

      // iban validation
      const ibanValidator = this.isIBANRequired ?
        [
          Validators.pattern(Regex.alphabetAndSpacePattern),
          Validators.maxLength(34), Validators.minLength(15),
          Validators.required
        ]
        :
        [
          Validators.pattern(Regex.alphabetAndSpacePattern),
          Validators.maxLength(34), Validators.minLength(15)
        ];

      // account number validation
      const accountNumberValidator = !this.isFasterPayandGBP && !this.isIBANRequired ?
        [
          Validators.pattern(accountNumberPattern),
          Validators.required
        ] :
        [Validators.pattern(accountNumberPattern)];

      // bank code validation
      const bankCodeValidation = this.isBankCodeAndAccountMandatory ?
        [
          Validators.pattern(bankCodePattern),
          Validators.maxLength(50),
          Validators.required
        ] :
        [
          Validators.pattern(bankCodePattern),
          Validators.maxLength(50),
        ];

      this.formStep2.get('accountNumber').setValidators(accountNumberValidator);
      this.formStep2.get('accountNumber').updateValueAndValidity();
      this.formStep2.get('iban').setValidators(ibanValidator);
      this.formStep2.get('iban').updateValueAndValidity();
      this.formStep2.get('bankCode').setValidators(bankCodeValidation);
      this.formStep2.get('bankCode').updateValueAndValidity();
      this.formStep2.setValidators(formValidator);
      this.formStep2.updateValueAndValidity();
    });
  }

}
